import React, { useEffect, useMemo, useState } from 'react'
import {
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    getSmoothStepPath,
    useReactFlow,
} from '@xyflow/react'
import './custom-edge.scss'
import { useJourneyContext } from '../../context'
import { getJourneyDisplayItemType } from '../../../../_utils/journeys'
import { JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import { InvalidJourneyComponent } from './invalid-journey-component'
import { titleCase } from 'change-case'
import { StepType } from '@pushly/models/lib/enums/journeys'
import type { ElkEdgeSection } from 'elkjs/lib/elk-api'
import { buildPathFromSections } from '../branch-edge/branch-edge'
import { getEdgeAddButtonAnchor } from '../shared/edge-add-button-anchor'
import { useEdgeActions } from '../shared/use-edge-actions'
import { EdgeActionPopover } from '../shared/edge-action-popover'
import { isBranchingNode, getBranchingNodeData } from '../../utils/graph/traversal'
import { getNodeOutStepId } from '../../utils/graph/traversal'

/**
 * Walk forward from a node through passthrough steps to check if any
 * reachable meaningful step matches the given type (e.g. ACTION→ACTION).
 * Used by the edge warning to detect consecutive sends across branches.
 */
function hasConsecutiveTypeForward(
    node: JourneyNode,
    matchType: StepType,
    nodeMap: Map<string, JourneyNode>,
    visited: Set<string>,
): boolean {
    if (visited.has(node.id)) return false
    visited.add(node.id)

    const stepType = node.data.step.type

    // Meaningful step found — check if it matches
    if (stepType === StepType.ACTION || stepType === StepType.DELAY) {
        return stepType === matchType
    }

    // Passthrough — walk all successors
    const successorIds = getNodeSuccessorIds(node)
    for (const succId of successorIds) {
        const succNode = nodeMap.get(succId)
        if (succNode && hasConsecutiveTypeForward(succNode, matchType, nodeMap, visited)) {
            return true
        }
    }

    return false
}

function getNodeSuccessorIds(node: JourneyNode): string[] {
    if (isBranchingNode(node)) {
        const data = getBranchingNodeData(node)
        if (data) {
            return data.branches
                .map((b) => b.out_step_id)
                .filter((id): id is string | number => id != null)
                .map(String)
        }
    }
    const outStepId = getNodeOutStepId(node)
    return outStepId != null ? [outStepId] : []
}

export const CustomEdge = ({
                               id,
                               sourceX,
                               sourceY,
                               sourcePosition,
                               targetX,
                               targetY,
                               targetPosition,
                               source,
                               target,
                               data,
                               ...props
                           }: EdgeProps<JourneyEdge>) => {
    // region Custom Edge Definitions

    const [state] = useJourneyContext()
    const { getNode } = useReactFlow<JourneyNode>()

    // The card → first-step edge below a variant's perf card uses the SYNTHETIC
    // perf-card node as its source. That node has no canonical counterpart in
    // state.nodes, so add-step must be re-rooted onto the real split node + the
    // branch the card represents — otherwise handleAddNode can't resolve the source
    // and silently no-ops (popover closes, editor never opens). For all other edges
    // perfCardMeta is undefined and we use the raw source.
    const perfCardMeta = getNode(source)?.data?.uiMeta?.perfCardOf
    const { edgeActionOptions, showEdgeActions, setShowEdgeActions, handleAddNode } = useEdgeActions({
        source: perfCardMeta?.splitId ?? source,
        target,
        variantId: perfCardMeta?.branchKey,
    })

    const [showInvalidConnection, setShowInvalidConnection] = useState(false)

    // ELK-computed edge sections are checked inside useMemo hooks below
    // to ensure React properly reacts to data changes.

    // Calculate edge path — use ELK sections if available, otherwise getSmoothStepPath
    const edgePath = useMemo(() => {
        // Check inside the memo so we react to data changes
        const sections: ElkEdgeSection[] | undefined = data?.elkSections

        if (sections && sections.length > 0) {
            return buildPathFromSections(sections)
        }
        const [path] = getSmoothStepPath({
            sourceX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
            borderRadius: 12,
        })
        return path
    }, [data, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition])

    // Position the (+) add button via getEdgeAddButtonAnchor, which reads the ELK
    // edge sections (falling back to native coords only for section-less linear
    // chains) so the overlay sits on the visible path and lines up across sibling
    // variant lanes.
    const { addButtonX, addButtonY } = useMemo(
        () => getEdgeAddButtonAnchor({
            sections: data?.elkSections,
            sourceX,
            sourceY,
            targetX,
            targetY,
        }),
        [data, sourceX, sourceY, targetX, targetY],
    )

    // endregion

    // Target step type (canonical) for the convergence-marker check below.
    const targetStepType = state.nodes.find(n => n.id === target)?.data?.step?.type

    // watch for invalid connections and show UI warning when node->node incompatibility.
    // Walks forward through passthrough steps (split, condition, placeholder) to find
    // the next meaningful step type, so consecutive sends across branches are caught.
    // Compute against CANONICAL state.nodes (not the render graph): the render graph
    // lags a structural edit by one async ELK pass, so reading getNode/getNodes here
    // evaluated the stale pre-removal graph and left the warning stuck on until reload
    // (the effect deps are canonical, so it never re-ran once the render graph caught up).
    useEffect(() => {
        const sourceNode = state.nodes.find((n) => n.id === source)
        const targetNode = state.nodes.find((n) => n.id === target)
        if (!sourceNode || !targetNode) {
            setShowInvalidConnection(false)
            return
        }

        const sourceType = sourceNode.data.step.type
        const targetType = targetNode.data.step.type

        // Direct consecutive check for non-passthrough targets
        const passthroughTypes = new Set([
            StepType.VARIANT_SPLIT,
            StepType.CONDITION,
            StepType.PLACEHOLDER,
            StepType.TRIGGER,
            StepType.GLOBAL_TRIGGER,
            StepType.EXIT,
        ])

        let invalid = false

        if (!passthroughTypes.has(sourceType) && !passthroughTypes.has(targetType)) {
            // Both are meaningful (ACTION or DELAY) — direct comparison
            invalid = sourceType === targetType
        } else if (sourceType === StepType.ACTION && passthroughTypes.has(targetType)) {
            // Source is ACTION, target is passthrough — walk forward to find next meaningful step
            const nodeMap = new Map(state.nodes.map((n) => [n.id, n]))
            invalid = hasConsecutiveTypeForward(targetNode, sourceType, nodeMap, new Set())
        }

        setShowInvalidConnection(invalid)
    }, [source, target, state.nodes])

    const invalidConnectionMessage = useMemo(() => {
        const sourceType = state.nodes.find((n) => n.id === source)?.data?.step?.type

        return sourceType === StepType.ACTION
            ? 'Send steps cannot appear consecutively without a delay between them. Add a wait step or remove one of the sends.'
            : sourceType
                ? `${titleCase(
                    sourceType,
                )} Steps cannot be adjacent. Please remove one of the ${sourceType?.toLowerCase()}s or add a step between them.`
                : 'Invalid connection. Please remove one of the steps or add a step between them.'
    }, [source, state.nodes])

    // Hide arrow markers on edges targeting convergence dots for a cleaner look.
    // Edges originating FROM convergence dots keep their arrow so the exit connection is clear.
    const targetIsConvergence = targetStepType === StepType.PLACEHOLDER
        && state.nodes.find(n => n.id === target)?.data?.uiMeta?.placeholderRole === 'convergence'

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={targetIsConvergence ? undefined : props.markerEnd}
                      markerStart={props.markerStart}/>
            <EdgeLabelRenderer>
                {showInvalidConnection && (
                    <div
                        className="journey-invalid-label"
                        role="alert"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transform: `translate(-225%, -50%) translate(${addButtonX}px, ${addButtonY}px)`,
                        }}
                    >
                        <InvalidJourneyComponent content={invalidConnectionMessage}/>
                    </div>
                )}
                <EdgeActionPopover
                    addButtonX={addButtonX}
                    addButtonY={addButtonY}
                    edgeActionOptions={edgeActionOptions}
                    showEdgeActions={showEdgeActions}
                    setShowEdgeActions={setShowEdgeActions}
                    handleAddNode={handleAddNode}
                    mode={state.mode}
                    itemType={getJourneyDisplayItemType(state.journey, state.domain.ecommConfig?.itemType)}
                />
            </EdgeLabelRenderer>
        </>
    )
}
